import API from './api';

export const tradingService = {
  // Dashboard
  getDashboard: () => API.get('/api/trading/dashboard/'),

  // Security Lifecycle review (rename vs terminal) — Data tab
  listLifecycleReviews: (status = 'pending') =>
    API.get(`/api/trading/security-lifecycle/${status ? `?status=${status}` : ''}`),
  confirmLifecycleReview: (id, note = '') =>
    API.post(`/api/trading/security-lifecycle/${id}/confirm/`, { note }),
  rejectLifecycleReview: (id, note = '') =>
    API.post(`/api/trading/security-lifecycle/${id}/reject/`, { note }),
  reverseLifecycleReview: (id) =>
    API.post(`/api/trading/security-lifecycle/${id}/reverse/`, { confirm: true }),
  lifecycleStaleBars: () => API.get('/api/trading/security-lifecycle/stale-bars/'),
  lifecycleRescan: (apply = false) => API.post('/api/trading/security-lifecycle/stale-bars/', { apply }),
  lifecycleRenamePreview: (id, payload) => API.post(`/api/trading/security-lifecycle/${id}/rename-preview/`, payload),
  lifecycleRenameApply: (id, payload) => API.post(`/api/trading/security-lifecycle/${id}/rename-apply/`, { ...payload, confirm: true }),
  lifecycleEmptySleeves: (id) =>
    API.get(`/api/trading/security-lifecycle/${id}/empty-sleeves/`),
  // History tab + per-event sleeves with Stop (2026-10-21)
  lifecycleHistory: () => API.get('/api/trading/security-lifecycle/history/'),
  lifecycleSleeves: (id) => API.get(`/api/trading/security-lifecycle/${id}/sleeves/`),
  lifecycleStopSleeves: (id, strategyIds) =>
    API.post(`/api/trading/security-lifecycle/${id}/stop-sleeves/`, { strategy_ids: strategyIds, confirm: true }),
  // One page of cards ('all' = every status); the page follows `next` itself.
  listLifecycleReviewsPage: (status, page = 1) => {
    const q = new URLSearchParams();
    if (status && status !== 'all') q.set('status', status);
    q.set('page', String(page));
    return API.get(`/api/trading/security-lifecycle/?${q.toString()}`);
  },
  reportLifecycleReview: (payload) =>
    API.post('/api/trading/security-lifecycle/report/', payload),

  // Orders (real IBKR orders — NettedOrder rows)
  listOrders: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return API.get(`/api/trading/orders/${qs ? `?${qs}` : ''}`);
  },
  // View-only dry-run: how staged orders will net by (account, symbol) at the
  // next release. No IBKR, no writes — mirrors the live release netting.
  previewNettedOrders: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return API.get(`/api/trading/orders/netted-preview/${qs ? `?${qs}` : ''}`);
  },
  // Canary/shadow daily outcomes (read-only): v2 parity + order-lifecycle canary history.
  getCanaryOutcomes: (days = 21) => API.get(`/api/trading/canary-outcomes/?days=${days}`),

  cancelOrder: (id) => API.post(`/api/trading/orders/${id}/cancel/`),
  retryOrder: (id) => API.post(`/api/trading/orders/${id}/retry/`),
  // Deferred pre-open execution: release a staged order to IBKR now.
  releaseOrder: (id) => API.post(`/api/trading/orders/${id}/release/`),
  // Remove a staged (queued) order before release — never touches IBKR.
  voidOrder: (id) => API.post(`/api/trading/orders/${id}/void/`),
  // Cancel ONE strategy's slice of a staged order; re-nets the survivors.
  // action ∈ {'skip','deactivate','reactivate'} (validated server-side per kind).
  cancelOrderSlice: (orderId, strategyId, action) =>
    API.post(`/api/trading/orders/${orderId}/cancel-slice/`, { strategy: strategyId, action }),
  releaseAllOrders: (accountId, confirm = true) =>
    API.post('/api/trading/orders/release-all/', { account: accountId, confirm }),

  // Symbols
  getSymbols: (search = '') => API.get(`/api/trading/symbols/${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  addSymbols: (symbols) => API.post('/api/trading/add-symbols/', { symbols }),
  getSymbolPrices: (id, days = 365) => API.get(`/api/trading/symbols/${id}/prices/?days=${days}`),
  getConsolidatedPrices: (id, days = 365 * 5) => API.get(`/api/trading/symbols/${id}/consolidated-prices/?days=${days}`),

  // Data sync
  triggerSync: (symbols, mode = 'daily', daysBack = 5) =>
    API.post('/api/trading/trigger-sync/', { symbols, mode, days_back: daysBack }),
  getSyncStatus: () => API.get('/api/trading/sync-status/'),
  getOrphanGuard: () => API.get('/api/trading/orphan-guard/'),  // inactive-orphan + terminal-held positions/orders
  // Incident ledger (wish #18) — superuser only
  getIncidents: (days = 30) => API.get(`/api/auth/incidents/?days=${days}`),
  getIncident: (id) => API.get(`/api/auth/incidents/${id}/`),

  // Price integrity: continuity/divergence review for one ticker (read-only).
  // our split-only adjusted series vs vendor split-adjusted close + impossible-gap backstop.
  reviewPriceContinuity: (ticker, start = '') => {
    const qs = new URLSearchParams(
      Object.entries({ ticker, start }).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return API.get(`/api/trading/price-continuity-review/${qs ? `?${qs}` : ''}`);
  },

  // Price integrity: universe-wide scan snapshot (read-only) + admin rescan trigger.
  // scope: 'active' (active-strategy names, ~478) | 'all' (every priced security, ~8.5k).
  // Each scope has its own persisted snapshot, so switching scope never mixes results.
  getPriceContinuityScan: (scope = 'active') =>
    API.get(`/api/trading/price-continuity-scan/?scope=${encodeURIComponent(scope)}`),
  triggerPriceContinuityScan: (scope = 'active') =>
    API.post('/api/trading/price-continuity-scan/', { scope }),

  // CA remediation: dry-run classification snapshot + last apply report (read-only),
  // admin triggers to (a) scan for corrections and (b) apply the auto-safe subset.
  getPriceCorrection: (scope = 'active') =>
    API.get(`/api/trading/price-correction/?scope=${encodeURIComponent(scope)}`),
  triggerCorrectionScan: (scope = 'active') =>
    API.post('/api/trading/price-correction/', { action: 'scan', scope }),
  triggerCorrectionApply: (scope = 'active') =>
    API.post('/api/trading/price-correction/', { action: 'apply', scope }),
  triggerCorrectionApplyMask: (scope = 'active') =>
    API.post('/api/trading/price-correction/', { action: 'apply_mask', scope }),

  // Price integrity: day-by-day TR diff (ours vs vendor) for one ticker (read-only).
  // Drives the Universal-residue drill-down popup — returns every shared consecutive-day
  // pair's daily-return delta sorted largest-|delta|-first (+ split-only diffs alongside).
  getDailyTrComparison: (ticker, top = 400) => {
    const qs = new URLSearchParams(
      Object.entries({ ticker, top }).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return API.get(`/api/trading/daily-tr-comparison/${qs ? `?${qs}` : ''}`);
  },

  // Strategies
  getStrategies: () => API.get('/api/trading/strategies/'),
  getStrategy: (id) => API.get(`/api/trading/strategies/${id}/`),
  createStrategy: (data) => API.post('/api/trading/strategies/', data),
  updateStrategy: (id, data) => API.patch(`/api/trading/strategies/${id}/`, data),
  deleteStrategy: (id) => API.delete(`/api/trading/strategies/${id}/`),
  getBacktests: (strategyId) => API.get(`/api/trading/strategies/${strategyId}/backtests/`),
  getOrders: (strategyId) => API.get(`/api/trading/strategies/${strategyId}/orders/`),
  getPositions: (strategyId) => API.get(`/api/trading/strategies/${strategyId}/positions/`),
  getLiveSignals: (strategyId) => API.get(`/api/trading/strategies/${strategyId}/live_signals/`),
  getLiveIndicators: (strategyId) => API.get(`/api/trading/strategies/${strategyId}/live-indicators/`),
  downloadAuditExcel: (strategyId, params = {}) =>
    API.get(`/api/trading/strategies/${strategyId}/audit-export/`, {
      params,
      responseType: 'blob',
      timeout: 120000,
    }),
  toggleStrategyActive: (strategyId) => API.post(`/api/trading/strategies/${strategyId}/toggle_active/`),
  // Wishlist #40: withdraw a stop recorded while the model account's sheet was out (not applied yet)
  cancelStrategyStop: (strategyId) => API.post(`/api/trading/strategies/${strategyId}/cancel-stop/`),
  runSignalNow: (strategyId, body = {}) => API.post(`/api/trading/strategies/${strategyId}/run_signal_now/`, body),
  changeAllocation: (strategyId, allocationUsd) => API.post(`/api/trading/strategies/${strategyId}/change_allocation/`, { allocation_usd: allocationUsd }),
  // Capital as an AMOUNT (wishlist #27): {delta, note, preview}. preview with no delta → the sleeve's capital / value / spare cash.
  changeCapital: (strategyId, body) => API.post(`/api/trading/strategies/${strategyId}/change_capital/`, body),
  changeBasketCapital: (portfolioId, body) => API.post(`/api/trading/portfolios/${portfolioId}/change-capital/`, body),
  deployFromCombo: (body) => API.post('/api/trading/strategies/deploy-from-combo/', body),
  // Multi-account IBKR portfolio: optional accountId query param picks WHICH
  // account to query. Omit (or pass null) for the user's default account.
  getIBKRPortfolio: (accountId = null) => API.get(
    `/api/trading/ibkr/portfolio/${accountId ? `?account_id=${accountId}` : ''}`,
    { timeout: 20000 },
  ),
  // Re-fetch a strategy list scoped to one account (used by the TopBar
  // selector — switching accounts re-runs this with the new id).
  // Returns ALL strategies for the account. The list endpoint is globally
  // paginated (PAGE_SIZE=50), so a single GET silently truncates to the first
  // 50 of N. Follow the `next` cursor until exhausted so callers always get
  // the full set (478+ for a deployed portfolio). Shape mirrors a plain GET:
  // resolves to { data: Strategy[] }.
  getStrategiesForAccount: async (accountId) => {
    const all = [];
    let url = `/api/trading/strategies/${accountId ? `?account_id=${accountId}` : ''}`;
    while (url) {
      const r = await API.get(url);
      const d = r.data;
      if (Array.isArray(d)) { all.push(...d); break; }          // pagination disabled
      all.push(...(d.results || []));
      // `next` is an absolute URL; strip origin so the axios baseURL +
      // interceptors apply consistently regardless of host/proto.
      url = d.next ? d.next.replace(/^https?:\/\/[^/]+/, '') : null;
    }
    return { data: all };
  },
  // Pre-summed allocation hierarchy (class → portfolio → single strategies)
  // + by_symbol rollup + total. Custom action, NOT paginated — replaces the
  // old flat strategy list that was silently capped at 50 by DRF pagination.
  getStrategyAllocationTree: (accountId) => API.get(
    `/api/trading/strategies/allocation-tree/${accountId ? `?account_id=${accountId}` : ''}`,
  ),

  // Per-security, per-trading-day NETTED execution summary. One row per
  // (symbol, day): gross bought/sold + net, strategy attribution (via
  // realized_delta), a manual bucket (client_id 0 / unmatched fills), and the
  // account reserve's target-rounding delta. days = int or 'all' (default 30).
  getStrategyExecutions: (accountId, days = 30) => API.get(
    `/api/trading/strategies/executions/?days=${days}${accountId ? `&account_id=${accountId}` : ''}`,
  ),

  // Strategy NAV time series (Phase B — daily snapshots)
  getStrategyEquityCurve: (strategyId, params = {}) => {
    // params: { start?: 'YYYY-MM-DD', end?: 'YYYY-MM-DD', limit?: 365 }
    const q = new URLSearchParams(params).toString();
    return API.get(`/api/trading/strategies/${strategyId}/equity-curve/${q ? `?${q}` : ''}`);
  },
  getPortfolioEquityCurve: (params = {}) => {
    // params: { account_id?, start?, end? }. Account-level rollup of all
    // member strategies' StrategyDailySnapshot rows.
    const q = new URLSearchParams(params).toString();
    return API.get(`/api/trading/portfolio-equity-curve/${q ? `?${q}` : ''}`);
  },
  getPortfolioTwr: (accountId = null, opts = {}) => {
    // NEW time-weighted return (per-epoch + portfolio) + old money-weighted +
    // SPY total-return series over the same window. Feeds the TWR + Comparison tabs.
    // `accountId` may be a single id or an array (subset consolidation). `opts` narrows
    // further: { portfolioId } (a basket) or { strategyIds } (specific strategies).
    const p = new URLSearchParams();
    if (Array.isArray(accountId)) { if (accountId.length) p.set('account_ids', accountId.join(',')); }
    else if (accountId != null) p.set('account_id', accountId);
    if (opts.portfolioIds && opts.portfolioIds.length) p.set('portfolio_ids', opts.portfolioIds.join(','));
    else if (opts.portfolioId != null) p.set('portfolio_id', opts.portfolioId);
    if (opts.strategyIds && opts.strategyIds.length) p.set('strategy_ids', opts.strategyIds.join(','));
    // View-time capital-mode override (Phase B): 'pool' | 'per_deploy'. Recomputes this response
    // in the requested mode without persisting; honored only at single-account scope.
    if (opts.mode) p.set('mode', opts.mode);
    // light=1: curve + benchmark only — skips the ~30s per-strategy epochs table and the
    // old ghost curve. Used by the Portfolio Overview, which never renders either.
    if (opts.light) p.set('light', '1');
    const q = p.toString();
    return API.get(`/api/trading/portfolio-twr/${q ? `?${q}` : ''}`);
  },
  getCommissionScenarioTwr: (accountId, liquidity = 'remove') => {
    // Commission-scenario TWR (Performance-tab panel): three full inception→today curves —
    // Actual / Tiered / At-scale — for ONE account. The frontend rebases each per range with the
    // same twrRange helpers as the main graph. Read-only; Actual == the page's live TWR.
    const p = new URLSearchParams();
    if (accountId != null) p.set('account_id', accountId);
    if (liquidity) p.set('liquidity', liquidity);
    const q = p.toString();
    return API.get(`/api/trading/commission-scenario-twr/${q ? `?${q}` : ''}`);
  },
  getDividendEvents: (accountId, opts = {}) => {
    // Dividend operator view (Performance-tab panel): every accrual / payable the account carries —
    // sleeves (from stored snapshots), central-book short payable, reserve residual — with ex / pay /
    // settle dates and stage (open / settled). Read-only; empty while the lifecycle seam is off.
    const p = new URLSearchParams();
    if (accountId != null) p.set('account_id', accountId);
    if (opts.start) p.set('start', opts.start);
    if (opts.asOf) p.set('as_of', opts.asOf);
    const q = p.toString();
    return API.get(`/api/trading/dividend-events/${q ? `?${q}` : ''}`);
  },
  getPortfolioRisk: (opts = {}) => {
    // Risk metrics (tracking error, drawdown, VaR/CVaR, beta, concentration). Same scope.
    const p = new URLSearchParams();
    if (opts.accountIds && opts.accountIds.length) p.set('account_ids', opts.accountIds.join(','));
    if (opts.portfolioIds && opts.portfolioIds.length) p.set('portfolio_ids', opts.portfolioIds.join(','));
    if (opts.strategyIds && opts.strategyIds.length) p.set('strategy_ids', opts.strategyIds.join(','));
    if (opts.benchmark) p.set('benchmark', opts.benchmark);
    if (opts.start) p.set('start', opts.start);
    if (opts.end) p.set('end', opts.end);
    if (opts.rollingWindow) p.set('rolling_window', opts.rollingWindow);
    const q = p.toString();
    return API.get(`/api/trading/portfolio-risk/${q ? `?${q}` : ''}`);
  },
  getPortfolioAttribution: (opts = {}) => {
    // Contribution to portfolio TWR: strategy (exact) + security (approx). Same scope params.
    const p = new URLSearchParams();
    if (opts.accountIds && opts.accountIds.length) p.set('account_ids', opts.accountIds.join(','));
    if (opts.portfolioIds && opts.portfolioIds.length) p.set('portfolio_ids', opts.portfolioIds.join(','));
    if (opts.strategyIds && opts.strategyIds.length) p.set('strategy_ids', opts.strategyIds.join(','));
    if (opts.start) p.set('start', opts.start);
    if (opts.end) p.set('end', opts.end);
    const q = p.toString();
    return API.get(`/api/trading/portfolio-attribution/${q ? `?${q}` : ''}`);
  },
  getPortfolioActiveAttribution: (opts = {}) => {
    // Active (Brinson-Fachler) attribution vs SPY — where the ALPHA came from. Same scope.
    const p = new URLSearchParams();
    if (opts.accountIds && opts.accountIds.length) p.set('account_ids', opts.accountIds.join(','));
    if (opts.portfolioIds && opts.portfolioIds.length) p.set('portfolio_ids', opts.portfolioIds.join(','));
    if (opts.strategyIds && opts.strategyIds.length) p.set('strategy_ids', opts.strategyIds.join(','));
    if (opts.start) p.set('start', opts.start);
    if (opts.end) p.set('end', opts.end);
    if (opts.benchmark) p.set('benchmark', opts.benchmark);
    const q = p.toString();
    return API.get(`/api/trading/portfolio-active-attribution/${q ? `?${q}` : ''}`);
  },
  getPortfolioLookthrough: (opts = {}) => {
    // ETF look-through exposure vs a benchmark (SPY). Same scope params as TWR.
    const p = new URLSearchParams();
    if (opts.accountIds && opts.accountIds.length) p.set('account_ids', opts.accountIds.join(','));
    if (opts.portfolioIds && opts.portfolioIds.length) p.set('portfolio_ids', opts.portfolioIds.join(','));
    if (opts.strategyIds && opts.strategyIds.length) p.set('strategy_ids', opts.strategyIds.join(','));
    if (opts.benchmark) p.set('benchmark', opts.benchmark);
    if (opts.date) p.set('date', opts.date);
    const q = p.toString();
    return API.get(`/api/trading/portfolio-lookthrough/${q ? `?${q}` : ''}`);
  },
  // Per-symbol reconciliation: strategy ledger qty vs Position-tracker qty.
  // Used by the Portfolio Monitor Strategy-Level toggle to surface drift
  // against the live broker panel.
  getStrategyPositions: (accountId = null) => API.get(
    `/api/trading/strategy-positions/${accountId ? `?account_id=${accountId}` : ''}`,
  ),

  // IBKR Accounts (multi-account support — Phase A of 2026-05-21 refactor)
  listIBKRAccounts:   ()         => API.get('/api/trading/ibkr-accounts/'),
  getIBKRAccount:     (id)       => API.get(`/api/trading/ibkr-accounts/${id}/`),
  createIBKRAccount:  (body)     => API.post('/api/trading/ibkr-accounts/', body),
  updateIBKRAccount:  (id, body) => API.patch(`/api/trading/ibkr-accounts/${id}/`, body),
  deleteIBKRAccount:  (id)       => API.delete(`/api/trading/ibkr-accounts/${id}/`),
  setDefaultIBKRAccount: (id)    => API.post(`/api/trading/ibkr-accounts/${id}/set-default/`),

  // Infusion Manager — account-scope external cash flows (infusions / withdrawals).
  listAccountCashFlows:   (accountId) => API.get(`/api/trading/account-cash-flows/${accountId ? `?account=${accountId}` : ''}`),
  createAccountCashFlow:  (body)      => API.post('/api/trading/account-cash-flows/', body),
  updateAccountCashFlow:  (id, body)  => API.patch(`/api/trading/account-cash-flows/${id}/`, body),
  deleteAccountCashFlow:  (id)        => API.delete(`/api/trading/account-cash-flows/${id}/`),

  // Interest-Income ledger — APPEND-ONLY account cash-income accruals (interest on idle cash).
  // income → equity: folds into the account TWR + NAV. GET + POST only (a correction is a new row).
  listAccountIncome:      (accountId) => API.get(`/api/trading/account-income/${accountId ? `?account=${accountId}` : ''}`),
  createAccountIncome:    (body)      => API.post('/api/trading/account-income/', body),
  deleteAccountIncome:    (id)        => API.delete(`/api/trading/account-income/${id}/`),

  // Deployments tab: current live deployments (grouped by basket) + the append-only
  // capital-flow timeline (deploy / withdraw / allocation change). Read-only.
  getDeploymentActivity: (params = {}) => API.get('/api/trading/deployment-activity/', { params }),
  // Model portfolio (off-brokerage account): create + Bloomberg order download / fill upload.
  createModelAccount: (body)     => API.post('/api/trading/ibkr-accounts/create-model/', body),
  downloadModelOrders: (accountId, commit = true, format = 'xlsx') =>
    API.get(`/api/trading/model-portfolio/${accountId}/download-orders/`, {
      params: { commit: commit ? 1 : 0, format },
      responseType: 'blob',
      timeout: 60000,
    }),
  emailModelOrders: (accountId, body) =>
    API.post(`/api/trading/model-portfolio/${accountId}/email-orders/`, body, { timeout: 60000 }),
  // Automated order-email config (recipients / instructions / enable toggle for the 05:00 ET beat).
  getOrderEmailSettings: (accountId) =>
    API.get(`/api/trading/model-portfolio/${accountId}/order-email-settings/`),
  updateOrderEmailSettings: (accountId, body) =>
    API.patch(`/api/trading/model-portfolio/${accountId}/order-email-settings/`, body),
  sendOrderEmailNow: (accountId) =>
    API.post(`/api/trading/model-portfolio/${accountId}/send-order-email-now/`, {}, { timeout: 60000 }),
  uploadModelFills: (accountId, file, dryRun = false) => {
    const form = new FormData();
    form.append('file', file);
    return API.post(`/api/trading/model-portfolio/${accountId}/upload-fills/`, form, {
      params: { dry_run: dryRun ? 1 : 0 },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  // Record the UNMATCHED fills from an uploaded sheet into the model account's error bucket
  // (opt-in; matched fills go through upload-fills unchanged). dryRun=true previews the count.
  recordUnmatchedToBucket: (accountId, file, dryRun = false) => {
    const form = new FormData();
    form.append('file', file);
    return API.post(`/api/trading/model-portfolio/${accountId}/record-unmatched/`, form, {
      params: { dry_run: dryRun ? 1 : 0 },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  // RECONCILIATION UPLOAD (safe inbox): parse + STORE a raw fills/positions workbook WITHOUT
  // applying it — for a by-hand reconcile the normal matcher can't do. kind: 'fills' | 'positions'.
  // Never touches strategies / positions / cash / the error bucket.
  reconcileUpload: (accountId, file, kind = 'fills') => {
    const form = new FormData();
    form.append('file', file);
    return API.post(`/api/trading/model-portfolio/${accountId}/reconcile-upload/`, form, {
      params: { kind },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  listReconcileUploads: (accountId) =>
    API.get(`/api/trading/model-portfolio/${accountId}/reconcile-upload/`),
  // Error bucket: list current entries (marked to latest close) / reverse a symbol's records.
  getErrorBucket: (accountId) =>
    API.get(`/api/trading/model-portfolio/${accountId}/error-bucket/`),
  reverseErrorBucket: (accountId, symbol) =>
    API.post(`/api/trading/model-portfolio/${accountId}/error-bucket/`, { symbol }),
  // Confirm "no fills" — clears the unresolved-batch guard and runs the same post-fill pipeline as a fills
  // upload (drift → rebalance if due → new sheet) now, unless it is US trading hours, when the 19:30 ET evening
  // run builds it. Response: { decision: 'now' | 'at_evening', regenerated_now, reason }.
  modelNoFills: (accountId) =>
    API.post(`/api/trading/model-portfolio/${accountId}/no-fills/`),
  // Manually regenerate this model account's staged order sheet now ("Run now"). 409 if a
  // generation is already in flight.
  regenerateModelSignals: (accountId) =>
    API.post(`/api/trading/model-portfolio/${accountId}/regenerate/`),
  // Operator audit trail for one account — who did what, when (download/email/upload-fills/
  // no-fills/regenerate/infusion add-edit-delete). Scoped server-side to accessible accounts.
  getModelAudit: (accountId, limit = 200) =>
    API.get(`/api/trading/model-portfolio/${accountId}/audit/`, { params: { limit } }),
  // IBKR Gateway lifecycle (separate-mode accounts only). The actual
  // login + IBKey push happens asynchronously on the trading EC2 —
  // poll connectionStatus to track state.
  connectIBKRAccount:    (id)    => API.post(`/api/trading/ibkr-accounts/${id}/connect/`),
  disconnectIBKRAccount: (id)    => API.post(`/api/trading/ibkr-accounts/${id}/disconnect/`),
  getIBKRConnectionStatus: (id, fresh = false) => API.get(
    `/api/trading/ibkr-accounts/${id}/connection-status/${fresh ? '?fresh=1' : ''}`,
  ),
  // Send the 4-8 digit SMS/email OTP that the IBKR gateway is waiting
  // on. Body shape: { otp: "123456" }. Backend types it into the Second
  // Factor dialog via xdotool. Returns the resulting state.
  submitIBKROtp: (id, otp) => API.post(
    `/api/trading/ibkr-accounts/${id}/submit-otp/`, { otp },
  ),
  // Re-login the IBKR gateway (re-run IBC login + SSL/socat re-patch on the
  // trading EC2 over SSM). Async — poll connection-status / re-fetch portfolio
  // afterwards to confirm reachability.
  reloginIBKRGateway: (id) => API.post(`/api/trading/ibkr-accounts/${id}/relogin/`),

  // Strategy Portfolios (Portfolio Builder R2/R3/R4)
  listPortfolios:       ()       => API.get('/api/trading/portfolios/'),
  getPortfolio:         (id)     => API.get(`/api/trading/portfolios/${id}/`),
  // cascadeStrategies=true also deletes member strategies used ONLY in this basket and
  // inactive (guarded server-side; shared/active/holding strategies are always kept).
  deletePortfolio:      (id, { cascadeStrategies = false } = {}) =>
    API.delete(`/api/trading/portfolios/${id}/${cascadeStrategies ? '?cascade_strategies=true' : ''}`),
  deployBasket:         (body)   => API.post('/api/trading/portfolios/deploy-basket/', body),
  togglePortfolioActive:(id)     => API.post(`/api/trading/portfolios/${id}/toggle-active/`),
  getPortfolioPerformance: (id)  => API.get(`/api/trading/portfolios/${id}/performance/`),
  // Sector-cascade drift snapshot (Phase A, measure-only). Read-only — never recomputes.
  getPortfolioRebalanceStatus: (id) => API.get(`/api/trading/portfolios/${id}/rebalance-status/`),
  getNetSignals:        (id)     => API.get(`/api/trading/portfolios/${id}/net-signals/`),
  executeNetSignals:    (id, body) => API.post(`/api/trading/portfolios/${id}/net-signals/`, body),
  // Model Portfolio: net PERCENTAGE weights per security (capital-base independent,
  // drift-immune, IBKR-free). Read-only compute used to drive a bigger book via BBG.
  getModelWeights:      (id)     => API.get(`/api/trading/portfolios/${id}/model-weights/`),
  // Backtest track-record analytics (regenerated per-member curves -> S3).
  getPortfolioAnalytics:       (id) => API.get(`/api/trading/portfolios/${id}/analytics/`),
  regeneratePortfolioAnalytics:(id) => API.post(`/api/trading/portfolios/${id}/regenerate-analytics/`),
  // Per-member track record for ONE strategy in a portfolio (metrics + equity
  // curve + same-window SPX), reused by the strategy-detail modal.
  getPortfolioMemberAnalytics: (id, strategyId) =>
    API.get(`/api/trading/portfolios/${id}/member-analytics/`, { params: { strategy_id: strategyId } }),
  // One optimization combo's full metrics by index (blended/IS/OOS + B&H benchmarks).
  getOptimizationCombo: (runId, comboIndex) =>
    API.get(`/api/trading/optimization/${runId}/combo/${comboIndex}/`),

  // Backtesting
  getAvailableStrategies: () => API.get('/api/trading/available-strategies/'),
  backtestPreflight: (data) => API.post('/api/trading/backtest/preflight/', data),
  runBacktest: (data) => API.post('/api/trading/backtest/', data, { timeout: 120000 }),

  // Multi-symbol batch optimize (fan one config across a universe, e.g. all S&P 500)
  runOptimizeBatch: (data) => API.post('/api/trading/optimize-batch/', data, { timeout: 120000 }),
  listOptimizeBatches: () => API.get('/api/trading/optimize-batches/'),
  getOptimizeBatchStatus: (id, withFundamentals = false) =>
    API.get(`/api/trading/optimize-batch/${id}/${withFundamentals ? '?fundamentals=1' : ''}`),
  cancelOptimizeBatch: (id) => API.post(`/api/trading/optimize-batch/${id}/cancel/`),
  restartOptimizeBatch: (id) => API.post(`/api/trading/optimize-batch/${id}/restart/`),

  // Strategy Scanner
  startScan: (data) => API.post('/api/trading/strategy-scanner/', data),
  getScanResult: (id) => API.get(`/api/trading/strategy-scanner/${id}/`),

  // Portfolio Simulation
  getPortfolioSimulation: (id) => API.get(`/api/trading/portfolio-simulation/${id}/`),

  // Strategy Analytics (mirrors HF analytics for trading data)
  analyticsSummary: (data) => API.post('/api/trading/analytics/summary/', data),
  analyticsCumulative: (data) => API.post('/api/trading/analytics/cumulative/', data),
  analyticsCorrelation: (data) => API.post('/api/trading/analytics/correlation/', data),
  analyticsRolling: (data) => API.post('/api/trading/analytics/rolling/', data),
  analyticsDrawdown: (data) => API.post('/api/trading/analytics/drawdown/', data),
  analyticsCalendar: (data) => API.post('/api/trading/analytics/calendar/', data),
  analyticsComparison: (data) => API.post('/api/trading/analytics/comparison/', data),
  analyticsDetail: (data) => API.post('/api/trading/analytics/detail/', data),

  // Agent v2 (LangGraph)
  agentChat: (data) => API.post('/api/trading/agent/', data, { timeout: 120000 }),

  // Strategy Builder v1 (legacy)
  getBuilderModels: () => API.get('/api/trading/strategy-builder/models/'),
  strategyBuilderChat: (data) => API.post('/api/trading/strategy-builder/chat/', data, { timeout: 60000 }),
  strategyBuilderSave: (data) => API.post('/api/trading/strategy-builder/save/', data),
  getUserStrategies: () => API.get('/api/trading/user-strategies/'),
  getUserStrategyDetail: (id) => API.get(`/api/trading/user-strategies/${id}/detail/`),
  deleteUserStrategy: (id) => API.delete(`/api/trading/user-strategies/${id}/`),
  shareUserStrategy: (id) => API.post(`/api/trading/user-strategies/${id}/share/`),
  unshareUserStrategy: (id) => API.delete(`/api/trading/user-strategies/${id}/share/`),
  promoteUserStrategy: (id, body) => API.post(`/api/trading/user-strategies/${id}/promote/`, body, { timeout: 60000 }),
  getStrategySource: (key) => API.get(`/api/trading/strategies/source/?key=${encodeURIComponent(key)}`),
  getOptimizationStatus: (id) => API.get(`/api/trading/optimization/${id}/`),

  // Optimizations Analytics tab (R1)
  listOptimizations: (params = {}) => API.get('/api/trading/optimizations/', { params }),
  // Aggregated strategy → grid tiles (all of them, not one page). Drives the
  // tile navigation; leaf runs are then paged via listOptimizations(grid_key).
  getOptimizationsTree: (params = {}) => API.get('/api/trading/optimizations/tree/', { params }),
  // Run-config classes within one param-grid (filter bar / Path B deploy basket).
  // grid_key is REQUIRED; optional strategy filter. Returns {classes, facets, ...}.
  getConfigClasses: (params = {}) => API.get('/api/trading/optimizations/config-classes/', { params }),
  // ── Portfolio deploy from an optimization tree (cap-weighted basket) ──
  // Dry-run cap-weight preview (#72) — ASYNC job+poll (Tier 3). The POST enqueues
  // the heavy compute (cold FMP fundamentals + per-symbol dollar-ADV) on the
  // celery worker and returns 202 {job_id}; we then poll until the job is ready.
  // This decouples client latency from server compute, so the preview can grow to
  // minutes for a few-thousand-name basket without the browser ever timing out.
  // Returns the SAME shape the old synchronous call did ({ data: payload }) so the
  // caller is unchanged; throws an axios-like error ({response:{data:{error}}}) on
  // failure so existing error handling keeps working.
  previewPortfolioFromTree: async (body, onProgress) => {
    const enqueue = await API.post('/api/trading/portfolio-from-tree/preview/', body);
    const jobId = enqueue.data?.job_id;
    // Back-compat: a synchronous server (pre-async build) returns the payload inline.
    if (!jobId) return enqueue;

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    const POLL_MS = 1500;
    // The sector-cascade preview re-runs a backtest per name (~2.5s/name → many minutes
    // at ~500 names), and can wait in a backed-up queue first. A fixed ceiling either
    // false-timed-out a healthy long run or let a truly-stuck job hang. Instead: the
    // server heartbeats progress, so we give up only when progress STALLS (no `done`
    // advance for STALL_MS) — with a generous absolute cap as a final backstop.
    const STALL_MS = 180000;   // 3 min with zero forward progress → treat as stuck (the real safety net)
    const ABS_CAP_MS = 21600000; // 6 h — only a truly abandoned job hits this; a long/queued preview
                                 // keeps polling as long as the server heartbeats forward progress.
    const startedAt = Date.now();
    let lastDone = -1;
    let lastAdvanceAt = Date.now();
    const fail = (msg) => {
      const err = new Error(msg);
      err.response = { data: { error: msg } };
      return err;
    };

    // eslint-disable-next-line no-constant-condition
    while (true) {
      await sleep(POLL_MS);
      let res;
      try {
        res = await API.get(`/api/trading/portfolio-from-tree/preview/${jobId}/`);
      } catch (e) {
        // 404 = job expired / not owned → terminal. Anything else (network blip,
        // transient 5xx) → keep polling within the stall/abs-cap budget.
        if (e?.response?.status === 404) throw fail('Preview job not found or expired.');
        if (Date.now() - startedAt > ABS_CAP_MS) throw fail('Preview timed out. Please retry.');
        continue;
      }
      const state = res.data?.state;
      if (state === 'ready') return { data: res.data.result };
      if (state === 'failed') throw fail(res.data?.error || 'Preview failed.');
      // running/pending: surface progress + reset the stall clock on any advance.
      const prog = res.data?.progress;
      if (prog) {
        if (typeof onProgress === 'function') { try { onProgress(prog); } catch (_e) { /* ignore */ } }
        if (typeof prog.done === 'number' && prog.done > lastDone) {
          lastDone = prog.done; lastAdvanceAt = Date.now();
        }
      }
      const now = Date.now();
      if (now - startedAt > ABS_CAP_MS) throw fail('Preview timed out. Please retry.');
      if (now - lastAdvanceAt > STALL_MS) throw fail('Preview stalled (no progress). Please retry.');
    }
  },
  // Create a deploy plan (#73): launches the missing-name sweep if needed, parks
  // the plan in pending_optimization (sweep) or ready_to_deploy (fully covered).
  createPortfolioFromTree: (body) => API.post('/api/trading/portfolio-from-tree/', body),
  // Deploy a ready plan (#75): materializes the live cap-weighted portfolio,
  // activates members, and stages orders for the next open. Idempotent.
  // Deploy materializes the whole basket in ONE atomic request (create N strategies +
  // activate + stage orders), so a 500-name book runs far past the global 30s axios
  // timeout — which is what surfaced as "timeout of 30000ms exceeded" while the server
  // was still working. Server ceiling is 120s (nginx proxy_read_timeout + gunicorn
  // --timeout); wait just past it so we receive the server's real outcome (success or
  // its 504) instead of the client giving up first. The endpoint is atomic + idempotent,
  // so a genuine timeout rolls back cleanly and a retry is safe.
  deployPortfolioFromTree: (planId, body = null) =>
    API.post(`/api/trading/portfolio-from-tree/${planId}/deploy/`, body, { timeout: 125000 }),
  // Poll a deploy plan's lifecycle/status (#77): used by the pending UI to learn
  // when a missing-name sweep has settled (ready_to_deploy) or Deploy completed.
  getPortfolioFromTreePlan: (planId) => API.get(`/api/trading/portfolio-from-tree/${planId}/`),
  filterOptimizationCombos: (id, body) => API.post(`/api/trading/optimizations/${id}/filter/`, body),
  saveOptimizationCombos: (combos) => API.post('/api/trading/optimizations/save-combos/', { combos }),
  resumeOptimization: (id) => API.post(`/api/trading/optimizations/${id}/resume/`),
  cancelOptimization: (id) => API.post(`/api/trading/optimizations/${id}/cancel/`),
  resumeAsParallelOptimization: (id) => API.post(`/api/trading/optimizations/${id}/resume-parallel/`),
  deleteOptimization: (id) => API.delete(`/api/trading/optimizations/${id}/`),
  visualizeOptimization: (id, body) => API.post(`/api/trading/optimizations/${id}/visualize/`, body || {}),
  // NSR (Neighborhood Sharpe Robustness): post triggers async compute, get polls status.
  computeOptimizationNSR: (id, body) => API.post(`/api/trading/optimizations/${id}/nsr/`, body || {}),
  getOptimizationNSR: (id) => API.get(`/api/trading/optimizations/${id}/nsr/`),
  // OOS full-grid run: triggers run_oos_task on the spot worker (combos_streamed runs).
  runOptimizationOos: (id) => API.post(`/api/trading/optimizations/${id}/run-oos/`),
  // Full pipeline: smart stage detection (OOS → NSR → leaderboards), runs only what's missing.
  runFullPipeline: (id) => API.post(`/api/trading/optimizations/${id}/run-full-pipeline/`),
  // Compute-queue visibility (read-only) + superuser reconcile (revoke/purge orphans).
  getComputeQueue: () => API.get('/api/trading/compute-queue/'),
  reconcileComputeQueue: (dryRun = true) => API.post('/api/trading/compute-queue/reconcile/', { dry_run: dryRun }),
  // Targeted per-item withdraw: revoke + purge specific queued messages by task_id (superuser).
  purgeComputeQueueItems: (taskIds, dryRun = true) =>
    API.post('/api/trading/compute-queue/purge/', { task_ids: taskIds, dry_run: dryRun }),
  // OOS validation: run selected combos on the held-out test window.
  oosValidate: (id, body) => API.post(`/api/trading/optimizations/${id}/oos-validate/`, body),
  // Re-run a single combo backtest to retrieve its trade log (trades not persisted per-combo).
  // forceEngine: null (auto, prefers vectorbt) | 'backtesting.py' (force bt.py for comparison)
  // 120s timeout (like runBacktest): the endpoint re-runs a full backtest + fetches
  // the strategy's factor basket live, which can exceed the default 30s axios timeout.
  getComboTrades: (runId, comboId, forceEngine = null) => API.post(`/api/trading/optimizations/${runId}/combo-trades/`, { combo_id: comboId, ...(forceEngine ? { force_engine: forceEngine } : {}) }, { timeout: 120000 }),
  // Rich chart payload: candles + equity + trades + IS/OOS/out-of-OOS regions (history→today).
  getComboChart: (runId, comboId) => API.post(`/api/trading/optimizations/${runId}/combo-chart/`, { combo_id: comboId }, { timeout: 120000 }),
  // Sector Rotation (RRG-style): RS-Ratio/RS-Momentum of 11 sector ETFs vs SPY, weekly, with tails.
  getSectorRRG: (params = {}) => API.get('/api/trading/sector-rrg/', { params, timeout: 60000 }),

  // Portfolio Combo Weight Optimizer
  listPortfolioCombos:  ()         => API.get('/api/trading/portfolio-combos/'),
  getPortfolioCombo:    (id)       => API.get(`/api/trading/portfolio-combos/${id}/`),
  createPortfolioCombo: (body)     => API.post('/api/trading/portfolio-combos/', body),
  deletePortfolioCombo: (id)       => API.delete(`/api/trading/portfolio-combos/${id}/`),
  rerunPortfolioCombo:  (id, body) => API.post(`/api/trading/portfolio-combos/${id}/rerun/`, body),
};
